import { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enAU } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { supabase } from '../lib/supabase';
import { P } from '../styles/theme';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }), // Monday start
  getDay,
  locales: { 'en-AU': enAU },
});

const STATUS_COLOR = {
  confirmed: P.green,
  pending:   P.amber,
  completed: P.deep,
  no_show:   '#8B3A3A',
  cancelled: '#B0A0A0',
};

const STATUS_LABEL = {
  confirmed: 'Confirmed',
  pending:   'Pending',
  completed: 'Completed',
  no_show:   'No Show',
  cancelled: 'Cancelled',
};

export default function CalendarView() {
  const [bookings,  setBookings]  = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [view,      setView]      = useState('month');
  const [date,      setDate]      = useState(new Date());
  const [filter,    setFilter]    = useState('all');
  const [loading,   setLoading]   = useState(true);

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('bookings')
      .select('*')
      .eq('archived', false)
      .order('booking_date', { ascending: true });
    setBookings(data || []);
    setLoading(false);
  };

  const events = useMemo(() => {
    const source = filter === 'all'
      ? bookings
      : bookings.filter(b => b.status === filter);

    return source.map(b => {
      const start = new Date(b.booking_date);
      const end   = new Date(start.getTime() + (b.total_duration || 60) * 60000);
      return {
        id:       b.id,
        title:    b.client_name,
        start,
        end,
        resource: b,
      };
    });
  }, [bookings, filter]);

  const eventStyleGetter = ({ resource }) => ({
    style: {
      backgroundColor: STATUS_COLOR[resource.status] || P.amber,
      borderRadius:    '5px',
      border:          'none',
      color:           '#fff',
      fontSize:        '11px',
      fontWeight:      700,
      padding:         '2px 5px',
      cursor:          'pointer',
    },
  });

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    Object.keys(STATUS_COLOR).forEach(s => {
      c[s] = bookings.filter(b => b.status === s).length;
    });
    return c;
  }, [bookings]);

  const sel = selected;
  const selDate = sel ? new Date(sel.booking_date) : null;

  return (
    <div>
      <style>{`
        .rbc-calendar            { font-family: 'Nunito', sans-serif; }
        .rbc-header              { background: ${P.peach}40; color: ${P.deep}; font-weight: 700; font-size: 12px; padding: 8px 4px; }
        .rbc-today               { background: ${P.peach}30 !important; }
        .rbc-off-range-bg        { background: ${P.bg}; }
        .rbc-event               { border-radius: 5px !important; }
        .rbc-event:focus         { outline: none; }
        .rbc-show-more           { color: ${P.rose}; font-weight: 700; font-size: 11px; }
        .rbc-toolbar button      { color: ${P.deep}; border: 1.5px solid ${P.border}; border-radius: 8px; font-family: 'Nunito', sans-serif; font-weight: 700; font-size: 12px; padding: 6px 12px; cursor: pointer; background: #fff; }
        .rbc-toolbar button:hover       { background: ${P.peach}40; }
        .rbc-toolbar button.rbc-active  { background: ${P.rose}; color: #fff; border-color: ${P.rose}; }
        .rbc-toolbar-label       { font-weight: 700; color: ${P.deep}; font-size: 15px; font-family: 'Cormorant Garamond', serif; }
        .rbc-month-row           { min-height: 90px; }
        .rbc-date-cell           { font-size: 12px; color: ${P.muted}; padding: 4px 6px; }
        .rbc-date-cell.rbc-now   { color: ${P.rose}; font-weight: 800; }
      `}</style>

      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600, marginBottom: '4px' }}>
        Booking Calendar
      </h2>
      <p style={{ fontSize: '13px', color: P.muted, marginBottom: '16px' }}>
        {bookings.length} total bookings
      </p>

      {/* Status filter chips */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {[
          { key: 'all',       label: `All (${counts.all})` },
          { key: 'pending',   label: `Pending (${counts.pending || 0})` },
          { key: 'confirmed', label: `Confirmed (${counts.confirmed || 0})` },
          { key: 'completed', label: `Completed (${counts.completed || 0})` },
          { key: 'no_show',   label: `No Show (${counts.no_show || 0})` },
          { key: 'cancelled', label: `Cancelled (${counts.cancelled || 0})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{
              padding: '5px 12px', borderRadius: '20px', border: `1.5px solid ${filter === key ? (STATUS_COLOR[key] || P.rose) : P.border}`,
              background: filter === key ? (STATUS_COLOR[key] || P.rose) + '18' : 'transparent',
              color: filter === key ? (STATUS_COLOR[key] || P.rose) : P.muted,
              cursor: 'pointer', fontSize: '11px', fontWeight: 700,
            }}>
            {label}
          </button>
        ))}
      </div>

      {/* Calendar */}
      <div style={{ background: '#fff', borderRadius: '14px', border: `1px solid ${P.border}`, padding: '16px', marginBottom: '16px', boxShadow: '0 2px 10px rgba(139,94,87,0.06)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: P.muted }}>Loading...</div>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            view={view}
            date={date}
            onView={setView}
            onNavigate={setDate}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={e => setSelected(e.resource)}
            style={{ height: view === 'month' ? 560 : 620 }}
            views={['month', 'week', 'day']}
            popup
            culture="en-AU"
            formats={{
              timeGutterFormat: 'h:mm a',
              eventTimeRangeFormat: ({ start, end }) =>
                `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
              agendaTimeRangeFormat: ({ start, end }) =>
                `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
            }}
            tooltipAccessor={e =>
              `${e.resource.client_name}\n${e.resource.service_name || ''}\n${STATUS_LABEL[e.resource.status] || e.resource.status}`
            }
          />
        )}
      </div>

      {/* Selected booking detail card */}
      {sel && (
        <div style={{ background: '#fff', borderRadius: '14px', border: `2px solid ${STATUS_COLOR[sel.status] || P.border}`, padding: '18px', boxShadow: '0 4px 16px rgba(139,94,87,0.10)', position: 'relative' }}>
          <button onClick={() => setSelected(null)}
            style={{ position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', fontSize: '18px', color: P.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: STATUS_COLOR[sel.status] || P.amber, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', fontWeight: 700, color: STATUS_COLOR[sel.status] || P.amber, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {STATUS_LABEL[sel.status] || sel.status}
            </span>
          </div>

          <h3 style={{ fontSize: '20px', color: P.deep, fontWeight: 700, marginBottom: '2px' }}>{sel.client_name}</h3>
          {sel.client_phone && <p style={{ fontSize: '13px', color: P.muted, marginBottom: '2px' }}>📞 {sel.client_phone}</p>}
          {sel.client_email && <p style={{ fontSize: '13px', color: P.muted, marginBottom: '10px' }}>✉️ {sel.client_email}</p>}

          <p style={{ fontSize: '14px', fontWeight: 700, color: P.text, marginBottom: '4px' }}>
            📅 {selDate && selDate.toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
          </p>
          {sel.service_name && (
            <p style={{ fontSize: '13px', color: P.deep, fontWeight: 600, marginBottom: '4px' }}>💅 {sel.service_name}</p>
          )}
          <p style={{ fontSize: '13px', color: P.muted, marginBottom: '0' }}>
            ⏰ {sel.total_duration || 60} min &nbsp;|&nbsp;
            💰 Total ${sel.total_amount} &nbsp;|&nbsp;
            Deposit ${sel.deposit_amount} &nbsp;|&nbsp;
            <span style={{ color: P.rose, fontWeight: 700 }}>Due ${sel.balance_due}</span>
          </p>
          {sel.has_allergy && (
            <p style={{ fontSize: '12px', color: P.red, fontWeight: 700, marginTop: '8px' }}>⚠️ Allergy reported</p>
          )}
        </div>
      )}
    </div>
  );
}
